<template>
  <base-layout :key="refreshKey">
    <!-- Sidebar Content -->
    <template #sidebar>
      <base-sidebar-menu
        :items="items"
        :collapsed.sync="$store.state.layout.sidebarCollapsed"
        :home-link="{ path: '/admin/dashboard' }"
        @update:collapsed="(val) => $store.commit('setSidebarCollapsed', val)"
      />
      <!-- Modals -->
      <edit-profile-modal
        v-if="$can('profile', 'User')"
        lazy-load
      />
      <edit-password-modal
        v-if="$can('update_password', 'User')"
        lazy-load
      />
      <sms-opt-in-modal
        v-if="$can('profile', 'User')"
        lazy-load
      />
      <change-organization-modal
        v-if="$can('change_organization', 'User')"
        :organization="organization"
        @on-save="() => $router.push({ name: 'admin-dashboard' })"
        lazy-load
      />
      <disable-mfa-auth-modal
        :email="user.email"
        @on-save="updateUser"
        lazy-load
      />
      <register-mfa-auth-modal
        :email="user.email"
        :qr-code="qrCode"
        @on-save="updateUser"
        lazy-load
      />
      <user-impersonation-modal
        v-if="$can('impersonate', 'User')"
        lazy-load
      />
    </template>
    <!-- END Sidebar Content -->
    <!-- Footer Content -->
    <template #footer>
      <div class="content py-3">
        <b-row>
          <b-col
            sm="12"
            order-sm="1"
            class="text-center"
          >
            <span class="mr-1">v{{ $appVersion }}</span>
            <a
              class="text-primary"
              href="http://scopesfs.com/"
              target="_blank"
            >
              {{ $store.getters.appName }}
              &copy; {{ $store.getters.appCopyright }}
            </a>
          </b-col>
        </b-row>
      </div>
    </template>
    <!-- END Footer Content -->
  </base-layout>
</template>

<script>
import { compact as _compact } from 'lodash';
import pluralize from 'pluralize';

import BaseLayout from '@/components/shared/layouts/Base';
import BaseSidebarMenu from '@/components/shared/BaseSidebarMenu';
import EditProfileModal from '@/components/shared/users/EditProfileModal.vue';
import EditPasswordModal from '@/components/shared/users/EditPasswordModal.vue';
import SmsOptInModal from '@/components/shared/users/SmsOptInModal.vue';
import ChangeOrganizationModal from '@/components/admin/users/ChangeOrganizationModal.vue';
import UserImpersonationModal from '@/components/admin/users/ImpersonationModal.vue';
import DisableMfaAuthModal from '@/components/auth/mfa/DisableMfaAuthModal.vue';
import RegisterMfaAuthModal from '@/components/auth/mfa/RegisterMfaAuthModal.vue';

import showAppOutOfDatePopup from '@/utils/showAppOutOfDatePopup.js';
import { SET_OUT_OF_DATE_MUTATION, SET_ORGANIZATION_MUTATION } from '@/constants/mutations.js';

import store from '@/store';

export default {
  name: 'AdminLayout',
  components: {
    BaseLayout,
    BaseSidebarMenu,
    EditProfileModal,
    EditPasswordModal,
    SmsOptInModal,
    ChangeOrganizationModal,
    UserImpersonationModal,
    DisableMfaAuthModal,
    RegisterMfaAuthModal,
  },
  data() {
    return {
      refreshKey: Date.now(),
      qrCode: null,
      items: _compact([
        {
          title: 'My Account',
          icon: 'far fa-circle-user',
          separator: true,
          child: _compact([
            {
              title: 'Settings',
              icon: 'fas fa-gear',
              child: _compact([
                {
                  title: 'Edit My Profile',
                  href: '',
                  icon: '',
                  modalId: 'edit-profile-modal',
                },
                {
                  title: 'SMS Notifications',
                  icon: '',
                  href: '',
                  modalId: 'sms-opt-in-modal',
                },
                {
                  title: 'Update My Password',
                  href: '',
                  icon: '',
                  modalId: 'update-password-modal',
                },
                this.$store.getters.user?.otp_verified && {
                  title: 'Disable MFA',
                  href: '',
                  icon: '',
                  modalId: 'disable-mfa-auth-modal',
                },
                !this.$store.getters.user?.otp_verified && {
                  title: 'Register MFA',
                  href: '',
                  icon: '',
                  modalId: 'register-mfa-auth-modal',
                },
              ]),
            },
            this.$can('change_organization', 'User') && {
              title: 'Change Organization',
              href: '',
              icon: '',
              modalId: 'change-organization-modal',
            },
            this.$can('impersonate', 'User') && {
              title: 'Impersonate Technician',
              href: '',
              icon: '',
              modalId: 'user-impersonation-modal',
            },
            {
              title: 'Logout',
              href: '/logout',
              icon: '',
            },
          ]),
        },
        {
          title: 'Work Orders',
          href: { path: '/admin/work_orders' },
          icon: 'fa fa-hammer',
          exactPath: true,
        },
        this.$can('read', 'Quote') && {
          title: 'Quotes',
          href: '/admin/quotes',
          icon: 'far fa-file-alt',
          exactPath: true,
        },
        {
          title: pluralize(this.$store.getters.translate('client', 'Client')),
          icon: 'far fa-hospital',
          child: _compact([
            {
              title: pluralize(this.$store.getters.translate('client', 'Client')),
              href: '/admin/clients',
              icon: 'far fa-hospital',
              exactPath: true,
            },
            {
              title: 'Locations',
              href: '/admin/locations',
              icon: 'far fa-compass',
              exactPath: true,
            },
            {
              title: 'Services',
              href: '/admin/services',
              icon: 'far fa-clipboard',
              exactPath: true,
            },
            {
              title: 'Feedback',
              href: '/admin/client_feedbacks',
              icon: 'far fa-chart-bar',
              exactPath: true,
            },
            {
              title: 'Walkthroughs',
              href: '/admin/walkthroughs',
              icon: 'far fa-file-alt',
              exactPath: true,
            },
          ]),
        },
        {
          title: 'Accounting',
          icon: 'fa fa-dollar-sign',
          child: _compact([
            this.$can('read', 'Invoice') && {
              title: 'Invoices',
              href: '/admin/invoices',
              icon: 'fa fa-dollar-sign',
              exactPath: true,
            },
            this.$can('read', 'TechnicianPayment') && {
              title: 'Tech Payments',
              href: '/admin/technician_payments',
              icon: 'far fa-credit-card',
              exactPath: true,
            },
            {
              title: 'Supplies',
              icon: 'fa fa-shipping-fast',
              child: _compact([
                {
                  title: 'Vendors',
                  icon: 'fa fa-shipping-fast',
                  exactPath: true,
                  href: '/admin/vendors',
                },
                {
                  title: 'Supply Orders',
                  icon: 'fas fa-envelope-open-text',
                  exactPath: true,
                  href: '/admin/supply_orders',
                },
                {
                  title: 'New Supply Order',
                  icon: 'fas fa-folder-plus',
                  exactPath: true,
                  href: '/admin/supply_orders/new',
                },
              ]),
            },
            this.$can('read', 'GLCode') && {
              title: 'GL Codes',
              icon: 'fa fa-project-diagram',
              exactPath: true,
              href: '/admin/gl_codes',
            },
            this.$can('read', 'TaxRate') && {
              title: 'Tax Rates',
              icon: 'fas fa-calculator',
              exactPath: true,
              href: '/admin/tax_rates',
            },
            this.$can('read', 'CommissionPlan') && {
                title: 'Commission Plans',
                icon: 'fas fa-flask',
                exactPath: true,
                href: '/admin/commission_plans',
            },
          ]),
        },
        {
          title: 'Users',
          icon: 'fas fa-users',
          separator: true,
          child: _compact([
            {
              title: 'Users',
              icon: 'fas fa-users',
              exactPath: true,
              href: '/admin/users',
            },
            this.$can('read', 'ComplianceDocument') && {
              title: 'Compliance Docs',
              href: '/admin/compliance_documents',
              icon: 'far fa-file-lines',
              exactPath: true,
            },
          ]),
        },
        {
          title: 'Reports',
          icon: 'far fa-chart-bar',
          child: _compact([
            {
              title: 'Work Orders',
              icon: '',
              child: _compact([
              this.$can('read_price', 'WorkOrderCharge') && {
                  title: 'Profitability',
                  href: '/admin/reports/work_orders/profitability',
                  icon: '',
                  exactPath: true,
                },
                this.$can('incentive_report', 'WorkOrder') && {
                  title: 'WO Incentive',
                  href: '/admin/reports/work_orders/incentive',
                  icon: '',
                  exactPath: true,
                },
                {
                  title: 'Overtime',
                  href: '/admin/reports/work_orders/overtime',
                  icon: '',
                  exactPath: true,
                },
                {
                  title: 'Total Over Time',
                  href: '/admin/reports/work_orders/total_over_time',
                  icon: '',
                  exactPath: true,
                }
              ])
            },
            this.$can('read', 'ClientFeedback') && {
              title: 'Client Feedback',
              icon: '',
              child: _compact([
                {
                  title: 'DSG KPI',
                  href: '/admin/reports/client_feedbacks/dicks_sporting_goods_kpi',
                  icon: 'fa fa-basketball-ball',
                  exactPath: true,
                },
              ])
            },
            this.$can('technician_report', 'User') && {
              title: 'Technicians',
              icon: '',
              child: _compact([
                {
                  title: 'KPI',
                  href: '/admin/reports/technicians/kpi',
                  icon: '',
                  exactPath: true,
                },
                {
                  title: 'Forecast',
                  href: '/admin/reports/technicians/forecast',
                  icon: '',
                  exactPath: true,
                }
              ])
            },
            this.$can('technician_report', 'User') && {
              title: 'Regional Manager KPI',
              href: '/admin/reports/technicians/regional_manager_kpi',
              icon: '',
              exactPath: true,
            },
            this.$can('revenue_report', 'Invoice') && {
              title: 'Revenue',
              href: '/admin/reports/invoices/revenue',
              icon: '',
              exactPath: true,
            },
            this.$can('read', 'Invoice') && {
              title: 'Tax',
              href: '/admin/reports/invoices/taxes',
              icon: '',
              exactPath: true,
            },
            this.$can('read', 'Commission') && {
              title: 'Commission Report',
              href: '/admin/reports/commissions/report',
              icon: '',
              exactPath: true,
            },
          ]),
        },
        {
          title: 'Admin',
          icon: 'fa fa-cog',
          child: _compact([
            this.$can('read', 'Import') && {
              title: 'Imports',
              href: '/admin/imports',
              icon: 'far fa-file',
              exactPath: true,
            },
            this.$can('read', 'Version') && {
              title: 'Audit Log',
              href: '/admin/versions',
              icon: 'fas fa-glasses',
              exactPath: true,
            },
            this.$can('read', 'DelayedJob') && {
              title: 'Delayed Jobs',
              href: '/admin/delayed_jobs',
              icon: 'far fa-clock',
              exactPath: true,
            },
            {
              title: 'Changelog',
              href: '/admin/changelog',
              icon: 'fa fa-rss',
              exactPath: true,
            },
          ]),
        },
      ]),
    };
  },
  created() {
    // Set default elements for this layout
    this.$store.commit('setLayout', {
      footer: true,
      sidebar: true,
    });

    this.$store.commit('mainContent', { mode: 'boxed' });
    this.$store.subscribe((mutation, state) => {
      if (mutation.type === SET_OUT_OF_DATE_MUTATION) {
        if (!state.app.upToDate && !this.$store.getters.hasUpToDatePopupRendered) {
          showAppOutOfDatePopup();
        }
      } else if (mutation.type === SET_ORGANIZATION_MUTATION) {
        this.refreshPage();
      }
    });
  },
  mounted() {
    if (!this.user.otp_verified) {
      this.registerMfaAuth();
    }
  },
  methods: {
    refreshPage() {
      this.refreshKey = Date.now();
    },
    updateUser(user) {
      if (!user.otp_verified) {
        this.registerMfaAuth();
      }
    },
    registerMfaAuth() {
      this.$authAPI.registerLoggedInUserMfaAuth().then((response) => {
        this.qrCode = response.data.otp_qr_code;
      });
    },
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    organization() {
      return this.$store.getters.currentOrganization;
    },
  },
  beforeRouteEnter(_to, _from, next) {
    store
      .dispatch('getProfile')
      .then(() => store.dispatch('getTerminology'))
      .then(() => next())
      .catch((error) => {
        if (error?.response?.status == 412) {
          showAppOutOfDatePopup();
        }
      });
  },
};
</script>
